import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, ShoppingBag, Eye, IndianRupee, UserCheck, UserX, Package, Truck, CheckCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-stats">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">Dashboard Overview</h1>
        <p className="text-[#525252] mt-1">Monitor your platform performance</p>
      </div>

      {/* User Stats */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Users</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-total-users">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-sm text-[#525252]">Total Users</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.users?.total || 0}</p>
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-active-users">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-500" />
              </div>
              <span className="text-sm text-[#525252]">Active Users</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.users?.active || 0}</p>
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-disabled-users">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-500" />
              </div>
              <span className="text-sm text-[#525252]">Disabled Users</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.users?.disabled || 0}</p>
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-recent-signups">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-500" />
              </div>
              <span className="text-sm text-[#525252]">This Week</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.users?.recent_signups || 0}</p>
          </div>
        </div>
      </div>

      {/* Order Stats */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Orders</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-total-orders">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#B8935A]/20 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-[#B8935A]" />
              </div>
              <span className="text-sm text-[#525252]">Total Orders</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.orders?.total || 0}</p>
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-pending-orders">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <span className="text-sm text-[#525252]">Pending</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.orders?.pending || 0}</p>
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-paid-orders">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-green-500" />
              </div>
              <span className="text-sm text-[#525252]">Paid</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.orders?.paid || 0}</p>
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-dispatched-orders">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-sm text-[#525252]">Dispatched</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.orders?.dispatched || 0}</p>
          </div>

          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-5" data-testid="stat-delivered-orders">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-600/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-[#525252]">Delivered</span>
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A]">{stats?.orders?.delivered || 0}</p>
          </div>
        </div>
      </div>

      {/* Revenue & Engagement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6" data-testid="stat-revenue">
          <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Revenue</h2>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center">
              <IndianRupee className="w-7 h-7 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-[#525252]">Total Revenue</p>
              <p className="text-4xl font-bold text-[#0A0A0A]">
                ₹{(stats?.revenue?.total || 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6" data-testid="stat-engagement">
          <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Platform Engagement</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-[#525252]">Profiles</p>
              <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.engagement?.profiles || 0}</p>
            </div>
            <div>
              <p className="text-sm text-[#525252]">Leads</p>
              <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.engagement?.leads || 0}</p>
            </div>
            <div>
              <p className="text-sm text-[#525252]">Total Views</p>
              <p className="text-2xl font-bold text-[#0A0A0A]">{stats?.engagement?.views || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
